import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import React from 'react'

import {
  PromptContentEditable,
  PromptEditorRoot,
  PromptPlaceholder,
} from './docChatPromptEditor.styles'
import { DocChatPromptEditorPlugin } from './DocChatPromptEditorPlugin'
import {
  $importDocChatPrompt,
  DOC_CHAT_PROMPT_NODES,
  DOC_CHAT_PROMPT_TRANSFORMERS,
} from './docChatPromptMarkdown'

function onEditorError(error: Error): never {
  throw error
}

export function DocChatPromptEditor({
  value,
  onChange,
  onKeyDown,
  onFocus,
  onPaste,
  editorRef,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  onKeyDown: (event: React.KeyboardEvent) => void
  onFocus: () => void
  onPaste: (event: React.ClipboardEvent) => void
  editorRef: React.RefObject<HTMLDivElement>
  placeholder: string
}) {
  const initialConfig = {
    // eslint-disable-next-line lingui/no-unlocalized-strings -- Internal Lexical namespace, never displayed.
    namespace: 'DocChatPrompt',
    nodes: DOC_CHAT_PROMPT_NODES,
    theme: {
      text: {
        bold: 'docchat-bold',
        italic: 'docchat-italic',
        code: 'docchat-code',
      },
    },
    editorState: () => $importDocChatPrompt(value),
    onError: onEditorError,
  }
  const handleKeyDownCapture = (event: React.KeyboardEvent) => {
    onKeyDown(event)
    if (
      event.defaultPrevented ||
      (event.key === 'Enter' && event.nativeEvent.isComposing)
    ) {
      event.stopPropagation()
    }
  }
  const handlePasteCapture = (event: React.ClipboardEvent) => {
    onPaste(event)
    if (event.defaultPrevented) event.stopPropagation()
  }

  return (
    <PromptEditorRoot>
      <LexicalComposer initialConfig={initialConfig}>
        <RichTextPlugin
          contentEditable={
            <PromptContentEditable
              ref={editorRef}
              aria-label={placeholder}
              aria-multiline
              onKeyDownCapture={handleKeyDownCapture}
              onPasteCapture={handlePasteCapture}
              onFocus={onFocus}
            />
          }
          placeholder={<PromptPlaceholder>{placeholder}</PromptPlaceholder>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <ListPlugin />
        <MarkdownShortcutPlugin transformers={DOC_CHAT_PROMPT_TRANSFORMERS} />
        <DocChatPromptEditorPlugin value={value} onChange={onChange} />
      </LexicalComposer>
    </PromptEditorRoot>
  )
}
